// src/pages/AddInterviewPrep.jsx
import React, { useState, useContext } from "react";
import { db, storage } from "./firebase/firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { UserContext } from "./context/UserContext";

const Wrapper = styled.div`
  padding: 40px;
  max-width: 900px;
  margin: auto;
  color: #fff;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #00d4ff;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  height: 120px;
  margin-bottom: 20px;
  border-radius: 8px;
  border: none;
  background: #1a1a1a;
  color: #fff;
  resize: vertical;
`;

const ThumbnailPreview = styled.img`
  width: 200px;
  height: auto;
  border-radius: 8px;
  margin-bottom: 15px;
  display: block;
`;

const Button = styled.button`
  padding: 12px 18px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #00d4ff, #00bcd4);
  color: #121212;
  font-weight: bold;
  cursor: pointer;

  &:disabled {
    background: #555;
    cursor: not-allowed;
  }
`;

const categories = [
  "React",
  "React Native",
  "JavaScript",
  "TypeScript",
  "Node.js",
  "Express.js",
  "Next.js",
  "Angular",
  "Vue.js",
  "Java",
  "Spring Boot",
  "Python",
  "Django",
  "Flask",
  "C++",
  "C#",
  "Go",
  "PHP",
  "Kotlin",
  "Swift",
  "SQL",
  "MongoDB",
  "PostgreSQL",
  "MySQL",
  "System Design",
  "DSA",
  "Algorithms",
  "Operating System",
  "CN (Computer Networks)",
  "DBMS",
  "OOPs",
  "Behavioral",
  "HR Interview",
  "DevOps",
  "Docker",
  "Kubernetes",
  "AWS",
  "Azure",
  "Google Cloud",
  "AI / Machine Learning",
  "Data Science",
  "Cybersecurity",
  "Blockchain",
  "Other",
];

const AddInterviewPrep = () => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Other");
  const [content, setContent] = useState("");
  const [type, setType] = useState("free");
  const [price, setPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(false);

  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!title || !content || !thumbnail) {
      alert("⚠️ Title, content and thumbnail are required");
      return;
    }

    if (type === "paid" && price <= 0) {
      alert("⚠️ Please enter a valid price for paid prep");
      return;
    }

    setLoading(true);
    try {
      // ✅ Upload thumbnail
      const storageRef = ref(
        storage,
        `interviewPreps/${Date.now()}-${thumbnail.name}`
      );
      await uploadBytes(storageRef, thumbnail);
      const thumbnailURL = await getDownloadURL(storageRef);

      // ✅ Save prep
      await addDoc(collection(db, "interviewPreps"), {
        title,
        category,
        content,
        type,
        price: type === "paid" ? Number(price) : 0,
        discount: type === "paid" ? Number(discount) : 0,
        thumbnail: thumbnailURL,
        author: user?.name || "Admin",
        createdAt: serverTimestamp(),
      });

      alert("✅ Interview prep added!");
      navigate("/admin-panel");
    } catch (err) {
      console.error("Error adding prep:", err);
      alert("❌ Error adding prep");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <h2>Add Interview Prep</h2>

      <Label>Prep Title</Label>
      <Input
        type="text"
        placeholder="Enter prep title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <Label>Category</Label>
      <Select value={category} onChange={(e) => setCategory(e.target.value)}>
        {categories.map((cat) => (
          <option key={cat} value={cat}>
            {cat}
          </option>
        ))}
      </Select>

      <Label>Type</Label>
      <Select value={type} onChange={(e) => setType(e.target.value)}>
        <option value="free">Free</option>
        <option value="paid">Paid</option>
      </Select>

      {type === "paid" && (
        <>
          <Label>Price (₹)</Label>
          <Input
            type="number"
            placeholder="Enter price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />

          <Label>Discount (%)</Label>
          <Input
            type="number"
            placeholder="Enter discount"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
        </>
      )}

      <Label>Thumbnail</Label>
      {thumbnail && (
        <ThumbnailPreview src={URL.createObjectURL(thumbnail)} alt="Preview" />
      )}
      <Input
        type="file"
        accept="image/*"
        onChange={(e) => setThumbnail(e.target.files[0])}
      />

      <Label>Description</Label>
      <TextArea
        placeholder="Short description or notes"
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />

      <Button onClick={handleSubmit} disabled={loading}>
        {loading ? "Saving..." : "Add Prep"}
      </Button>
    </Wrapper>
  );
};

export default AddInterviewPrep;
